/**
 * Skeleton — the loading placeholder for content that has not arrived yet.
 *
 * Corpus: `DB:279-281` (stacked text bars closing on a short
 * one), `DB:2952-2953` (each bar 14px high, full width then
 * 70%), `SI:38-42` and `JC:42-44`
 * (the same block inside an `aria-busy` region). Every file in the corpus
 * animates the fill with `prShimmer 1.4s ease-in-out infinite`
 * (`DB:20,1864`), and every run of bars ends narrow — hence the
 * taper, which reads as a paragraph rather than as a table. The taper floors at
 * 4%: a caller counting rows from data can ask for a tenth bar, and 100 - 9*12
 * is a negative width, which CSSOM discards — leaving the bar full width and
 * the block reading as the table the taper exists to avoid.
 *
 * The bars are hidden from assistive technology and the wrapper does the
 * announcing, as at `DB:260-262`: a reader should hear once that
 * something is loading, not hear eight blank bars. `label` is a prop because
 * that sentence is copy, and copy comes from the caller's catalogue.
 */
export interface SkeletonProps {
  /**
   * How many bars to draw. Normalised, not trusted: see `barCount`.
   */
  readonly lines?: number;
  readonly label: string;
}

/**
 * The most bars this will draw, whatever it is asked for.
 *
 * `lines` is a count a caller derives from data — rows expected, results
 * pending — so it arrives from wherever that number came from. `Array.from({
 * length })` takes it literally: `Infinity` throws a RangeError and kills the
 * render, and a plausible accident like a millisecond timestamp allocates until
 * the tab stops responding. Neither is a hypothetical about a hostile caller;
 * both are what an off-by-a-unit bug upstream looks like from in here. A
 * loading placeholder is the last thing that should be able to take down the
 * page it is standing in for, so the count is clamped and a bad one degrades to
 * the default rather than throwing.
 *
 * 24 is well past anything the corpus draws (four to six) and past the taper's
 * own floor at nine, so no honest caller reaches it.
 */
const MAX_LINES = 24;

function barCount(lines: number): number {
  if (!Number.isFinite(lines)) return 0;
  return Math.min(MAX_LINES, Math.max(0, Math.floor(lines)));
}

export function Skeleton({ lines = 3, label }: SkeletonProps) {
  return (
    <div aria-busy="true" aria-label={label} role="status">
      {Array.from({ length: barCount(lines) }, (_, index) => (
        <div
          key={index}
          className="rp-skeleton"
          aria-hidden="true"
          // Only the taper is inline, because only the taper varies with the
          // row. The bar's height and the gap below it are the same on every
          // bar, so they live in `skeleton.css` with the rest of its geometry.
          style={{ width: `${Math.max(4, 100 - index * 12)}%` }}
        />
      ))}
    </div>
  );
}
